import React from "react";
import { Modal } from "react-bootstrap";
import {
  invalidatePartnerAccessCache,
  useCancelMultiPassBooking,
} from "../../api";
import { Spacer } from "../../spacer";
import styles from "./cancel_booking_dialog.module.css";
import { useToast } from "../../../components";
import { PortalBookingType } from "../hooks/use_portal_booking_query";
import { useCancelSubscriptionBooking } from "../hooks/use_cancel_subscription_booking";
import { useAnalytics } from "@/lib/analytics/analytics";

interface CancelBookingDialogProps {
  show: boolean;
  onHide: () => void;
  bookingId: string;
  partnerId: string;
  space?: { id?: string; name?: string };
  bookingType?: PortalBookingType;
}

export function CancelBookingDialog({
  show,
  onHide,
  bookingId,
  partnerId,
  space,
  bookingType,
}: CancelBookingDialogProps) {
  const toast = useToast();
  const cancelMultiPassBooking = useCancelMultiPassBooking();
  const cancelMembershipBooking = useCancelSubscriptionBooking();
  const analytics = useAnalytics();

  const handleCancelBooking = async () => {
    onHide();
    try {
      if (bookingType) {
        if (bookingType === "access") {
          await cancelMultiPassBooking(bookingId);
          analytics.event("Cancel Multi Pass Booking", {
            "Booking UUID": bookingId,
            "Space UUID": space?.id,
            "Space Name": space?.name,
            Source: "Portal",
          });
        } else if (bookingType === "membership") {
          await cancelMembershipBooking(partnerId, bookingId);
          analytics.event("Cancel Membership Booking", {
            "Booking UUID": bookingId,
            Source: "Portal",
          });
        }
        toast.success({
          message: "Your booking has been successfully cancelled.",
        });
        await invalidatePartnerAccessCache(partnerId);
      }
    } catch {
      toast.error({ message: "Could not cancel booking" });
    }
  };

  return (
    <Modal show={show} onHide={onHide} centered>
      <Modal.Header className={styles.header} closeButton>
        <div>Cancel Booking</div>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.title}>
          Are you sure you want to cancel this booking?
        </div>
        <Spacer size={16} />
        <div>{`Your booking will be cancelled and your ${
          bookingType === "access"
            ? "multi"
            : bookingType === "membership"
            ? "membership"
            : ""
        } passes will be restored.`}</div>
      </Modal.Body>
      <Modal.Footer>
        <div className={styles.footerWrapper}>
          <button className={styles.notRightNowBtn} onClick={onHide}>
            Not right now
          </button>
          <button className={styles.cancelBtn} onClick={handleCancelBooking}>
            Yes, cancel
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}
